import React, { useEffect, useRef, useState } from 'react'
import { formatErrorMessage, noBcpGetPage, updateNoBcp, uploadFileForRow } from './NoBcp'
import { getIcon } from '../shared/utils/_icons.jsx'
import { CircularProgress, IconButton, List, ListItem, ListItemText, Avatar, Tooltip, Typography } from '@mui/material'
import { DeleteSharp, SaveSharp } from '@mui/icons-material'
import { ThemeProvider } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { DataGridPro, LicenseInfo, GridToolbar } from '@mui/x-data-grid-pro'
import { filterByValue, stringAvatar } from './Utils'
import PropTypes from 'prop-types'
import { Observable } from 'rxjs'
import { Config } from 'olympe'
import { reasonCodes } from './_enums';
import { RFDocumentTemplateLink } from './_snippets.jsx';
import { _theme } from '../shared/_theme';

/**
 *
 * @param {Object} props
 * @param {Observable} props.filters
 * @param {Object} props.reload
 * @param {string} props.searchText
 * @param {[]} props.rows
 * @param {function(boolean)} props.setReload
 * @param {function(Event, [])} props.multipleEdit
 * @param {function(Event, string)} props.throwError
 * @param {function(string[])} props.setRows
 * @param {function(boolean)} props.setOpenDescription
 * @param {function(string)} props.setCurrentDescription
 * @param {function(number)} props.setRowClicked
 * @param {function(*)} props.handleClickReasonCode
 * @param {function(*)} props.handleClickExpiry
 * @return {JSX.Element}
 * @constructor
 */
export function BCPDataGrid(props) {
    // Add the License for the DataGrid Pro
    const licenseKey = Config.getParameter('uiAppValues.datagrid_license')
    LicenseInfo.setLicenseKey(licenseKey)
    const [outageIndicator, setOutageIndicator] = useState(sessionStorage.getItem("Outage_indicator_session"))

    console.log(":: outageIndicator ::::", outageIndicator)
    let columns = []
    const pagesNextCursor = useRef({})

    useEffect(() => {
        // 2 hours in milliseconds: 2 * 60 * 60 * 1000
        const TWO_HOURS = 7200000;

        const intervalId = setInterval(() => {
            // Replace this with whatever logic or API call you need
            setOutageIndicator(sessionStorage.getItem("Outage_indicator_session"));
        }, TWO_HOURS);

        // Cleanup: clear the interval when the component unmounts
        return () => clearInterval(intervalId);
    }, []);

    const [selectionModel, setSelectionModel] = useState([])
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(15)
    const [loading, setLoading] = useState(false)

    // Handler the data refresh on page change or offset change
    const handlePageChange = newPage => {
        // We have the cursor, we can allow the page transition.
        if (newPage === 0 || pagesNextCursor.current[newPage - 1]) {
            setPage(newPage)
        }
    }

    /**
     * @param {Array<Object>} data
     * @param {string} operation
     * @return {Promise<void>}
     */
    const handleUpdateClick = async (data, operation) => {
        setLoading(true)

        // check for new files to upload in the single line that can be updated
        if (operation === 'PUT') {
            const errorMessage = await uploadFileForRow(data[0]);
            if (errorMessage !== null) {
                setLoading(false);
                props.throwError(null, errorMessage);
                return;
            }
        }

        // update the row
        const result = await updateNoBcp(data, operation)
        if (result.statusCode) {
            props.throwError(null, formatErrorMessage(result, 'An error occurred while updating the record'))
        }

        // refresh display
        await fetchMaterials(props.filters)
        setLoading(false)
    }

    const fetchMaterials = async filters => {
        const nextCursor = pagesNextCursor.current[page - 1]
        if (!nextCursor && page > 0) {
            return
        }

        const newRows = await noBcpGetPage(nextCursor, pageSize, filters)

        if (newRows.statusCode) {
            setLoading(false)
            props.throwError(null, formatErrorMessage(newRows))
        } else if (newRows.materials.length < 1) {
            setLoading(false)
            props.throwError(null, 'Empty response received. Please use different filters')
        }

        if (newRows.moreRecordsAvailable || newRows.nextCursor === null) {
            pagesNextCursor.current[page] = newRows.nextCursor
        }

        props.setRows(newRows.materials)
        setLoading(false)
    }

    useEffect(() => {
        (async () => {
            setLoading(true)
            await fetchMaterials(props.filters)
            setLoading(false)
        })()
    }, [page, pageSize, props.filters])

    if (props.rows && props.rows.length > 0) {
        props.rows.forEach((item, index) => {
            item.id = index

            let reasonCodeColor;
            if (item['reasonCode']) {
                reasonCodeColor = reasonCodes[item['reasonCode']]?.color;
            }
            if (reasonCodeColor === undefined) {
                reasonCodeColor = '#D00032';
            }
            item['reason-code-color'] = reasonCodeColor;

            if (item.newUploadedFile instanceof File) {
                item.fullDocumentName = item.newUploadedFile.name;
            } else if (item.document.fileID) {
                item.fullDocumentName = `${item.document.fileName}.${item.document.fileExt}`;
            }

        });

        /**
         * Row saving is disabled if its reasonCode is not supported, or if the required fields are not filled
         *
         * @param {Object} row
         * @return {boolean}
         */
        const rowEditionDisabled = (row) => {
            return !row.expiry || !row.comments || reasonCodes[row.reasonCode] === undefined
                || (row.reasonCode === 'RF' && row.fullDocumentName === '')
                || (Number(outageIndicator) !== 0);
        };

        columns = [
            {
                field: 'multipleEdit',
                headerName: 'Edit',
                width: 25,
                disableExport: true,
                resizable: false,
                renderHeader: () => {
                    return (
                        <div
                            style={{ display: 'flex', cursor: 'pointer' }}
                            onClick={event => {
                                // Handle the external reload if needed
                                // This method sucks, need to dispach event and stop using Actions for UI
                                props.reload.observe().subscribe(value => {
                                    if (value) {
                                        (async () => {
                                            setLoading(true)
                                            await fetchMaterials(props.filters)
                                            setLoading(false)
                                        })()
                                    }
                                    props.setReload(false)
                                })
                                const checkedItems = props.rows
                                    .map(item => ({
                                        ...item,
                                        checked: selectionModel.includes(item.id),
                                    }))
                                    .filter(item => item.checked)

                                if (checkedItems.length > 0) {
                                    props.multipleEdit(event, checkedItems)
                                } else {
                                    props.throwError(event, 'Please, select at least one material to edit.')
                                }
                            }}>
                            {getIcon('Edit', true, { style: { color: 'orange', minWidth: '25px' } })}
                        </div>
                    )
                },
                sortable: false,
            },
            {
                field: 'delete',
                headerName: 'delete',
                width: 25,
                disableExport: true,
                resizable: false,
                renderHeader: () => {
                    return (
                        <div
                            style={{ display: 'flex', cursor: 'pointer' }}
                            onClick={event => {
                                const checkedItems = props.rows
                                    .map(item => ({
                                        ...item,
                                        checked: selectionModel.includes(item.id),
                                    }))
                                    .filter(item => item.checked)

                                if (checkedItems.length > 0) {
                                    console.log('checkedItems', checkedItems)
                                    handleUpdateClick(checkedItems, 'DELETE')
                                } else {
                                    props.throwError(event, 'Please, select at least one material to delete.')
                                }
                            }}>
                            {getIcon('Delete', true, { style: { color: 'orange', minWidth: '25px' } })}
                        </div>
                    )
                },
                sortable: false,
            },
            {
                field: 'market',
                headerName: 'Market',
                minWidth: 120,
                resizable: true,
            },
            {
                field: 'plant',
                headerName: 'Plant',
                minWidth: 100,
                resizable: true,
                valueGetter: params => `${params.row.plantId || ''} - ${params.row.plantDescription || ''}`,

                renderCell: params => {
                    return <ListItemText primary={params.row.plantId} secondary={params.row.plantDescription} />
                },
            },
            {
                field: 'MATERIAL',
                headerName: 'Material',
                minWidth: 250,
                resizable: true,
                valueGetter: params => `${params.row.materialId || ''} - ${params.row.materialDescription || ''}`,

                renderCell: params => {
                    return (
                        <ListItemText
                            primary={parseInt(params.row.materialId)}
                            secondary={params.row.materialDescription}
                        />
                    )
                },
            },
            {
                field: 'spec',
                headerName: 'Specification',
                minWidth: 250,
                resizable: true,
                valueGetter: params =>
                    `${params.row.specificationCode || ''} - ${params.row.specificationDescription || ''}`,
                renderCell: params => {
                    return (
                        <ListItemText
                            primary={params.row.specificationCode}
                            secondary={params.row.specificationDescription}
                        />
                    )
                },
            },
            {
                field: 'vendor',
                headerName: 'Main vendor',
                minWidth: 250,
                height: 'auto',
                resizable: true,
                valueGetter: params =>
                    params.row.mainVendor && params.row.mainVendor[0]
                        ? `${params.row.mainVendor[0]['id']} - ${params.row.mainVendor[0]['description']}`
                        : '',
                renderCell: params => (
                    <List dense={true}>
                        {params.row.mainVendor.map(vendor => {
                            return (
                                <ListItem key={vendor.id + vendor.description} style={{ paddingTop: '1px' }}>
                                    <ListItemText primary={parseInt(vendor.id)} secondary={vendor.description} />
                                </ListItem>
                            )
                        })}
                    </List>
                ),
            },

            {
                field: 'vendorSite',
                headerName: 'Vendor site',
                minWidth: 250,
                resizable: true,
                valueGetter: params =>
                    `${params.row.vendorSiteNumber || ''} - ${params.row.vendorSiteDescription || ''}`,
                renderCell: params => {
                    return (
                        <ListItemText
                            primary={parseInt(params.row.vendorSiteNumber)}
                            secondary={params.row.vendorSiteDescription}
                        />
                    )
                },
            },
            {
                field: 'reasonCode',
                headerName: 'Reason Code',
                width: 250,
                resizable: false,
                renderCell: params => (
                    <div className={'component-with-icon-reasoncode'} disabled={rowEditionDisabled(params.row)}>
                        <div className={'reason-code'} style={{ backgroundColor: params.row['reason-code-color'] }}>
                            {params.row.reasonCode || ''}
                        </div>
                        <div className={'datagrid-icon-in-cell'} 
                            onClick={(event) => (props.handleClickReasonCode(event, params.row))}>
                            {getIcon('ExpandMore', true, { style: { fontSize: '19px' } })}
                        </div>
                        <div />
                    </div>
                ),
            },
            {
                field: 'modifiedBy',
                headerName: 'Modified By',
                width: 140,
                resizable: false,
                valueGetter: params => (params.row['modified-by'] ? params.row['modified-by']['full_name'] : ''),
                renderCell: params =>
                    params.row['modified-by'] && (
                        <Tooltip
                            title={`${params.row['modified-by']['full_name']} (${params.row['modified-by']['email']})`}>
                            <Avatar
                                {...stringAvatar(params.row['modified-by']['full_name'])}
                                style={{ margin: 'auto' }}
                            />
                        </Tooltip>
                    ),
            },
            {
                field: 'expiry',
                headerName: 'Next Review Date',
                minWidth: 140,
                resizable: true,
                renderCell: params => {
                    if (params.row.expiry) {
                        return (
                            <div className={'component-with-icon'} disabled={rowEditionDisabled(params.row)}
                                onClick={(event) => (!rowEditionDisabled(params.row) ? props.handleClickExpiry(event) : null)}>
                                {params.row.expiry.split('T')[0]}
                                <div className={'datagrid-icon-in-cell'}>
                                    {getIcon('ExpandMore', true, { style: { fontSize: '19px' } })}
                                </div>
                            </div>
                        )
                    } else {
                        return (
                            <div className={'component-with-icon'} onClick={event => props.handleClickExpiry(event)}>
                                -
                                <div className={'datagrid-icon-in-cell'}>
                                    {getIcon('ExpandMore', true, { style: { fontSize: '19px' } })}
                                </div>
                            </div>
                        )
                    }
                },
            },
            {
                field: 'description',
                headerName: 'Description',
                minWidth: 150,
                resizable: true,
                valueGetter: params => (params.row.comments ? params.row.comments : '-'),
                renderCell: params => (
                    <div
                        className={'datagrid-icon-in-cell'}
                        style={{ width: '100%' }}
                        onClick={() => {
                            props.setOpenDescription(true)
                            if (params.row.comments) {
                                props.setCurrentDescription(params.row.comments)
                            }
                        }}>
                        <div style={{ width: '100%', verticalAlign: 'middle' }}>
                            {getIcon('Subject', true, { style: { fontSize: '12px', marginRight: '4px' } })}
                            {params.row.comments || '-'}
                        </div>
                    </div>
                ),
            },
            {
                field: 'nnsStatus',
                headerName: 'NNS',
                minWidth: 75,
                resizable: true,
                pinnable: false,
                renderCell: (params) => {
                    return (
                        <ListItemText
                            primary={params.row.nnsStatus}
                        />
                    );
                },
            },
            {
                field: 'document',
                headerName: 'Document',
                minWidth: 150,
                resizable: true,
                valueGetter: params => (params.row.fullDocumentName || '-'),
                renderCell: params => (
                    <Typography
                        className="datagrid-icon-in-cell"
                        style={{ width: '100%', height: '100%', cursor: 'pointer' }}
                        onClick={() => {
                            // for all types of Reason code, if uploaded document is present then show the document details
                            if (params.row.fullDocumentName) {
                                props.setCurrentDocument(params.row.document);
                                props.setOpenDocument(true);
                            }
                            else {
                                props.setCurrentDocument(null);
                            }

                        }}>
                        {params.row.fullDocumentName ? <u>{params.row.fullDocumentName}</u> : '-'}
                    </Typography>
                ),
            },
            {
                field: 'psName',
                headerName: 'PS Name',
                minWidth: 150,
                resizable: true,
            },
            {
                field: 'pmName',
                headerName: 'PM Name',
                minWidth: 150,
                resizable: true,
            },
             {
                field: 'tastyClassification',
                headerName: 'Tasty Classification',
                minWidth: 150,
                resizable: true,
                valueGetter: params => (params.row['document'] ? params.row['document']['tastyClassification'] : ''),
                renderCell: (params) => {
                    return (
                        <ListItemText
                            primary={params.row['document']['tastyClassification']}
                        />
                    );
                },
            },
            {
                field: 'saveButton',
                headerName: '',
                width: 60,
                headerAlign: 'center',
                sortable: false,
                disableExport: true,
                resizable: false,
                renderHeader: () => (
                    <IconButton
                        aria-label="Save"
                        style={{ width: '100%', verticalAlign: 'middle', color: '#676665' }}
                        size="large"
                        title='Edit'
                        >
                        <SaveSharp />
                    </IconButton>
                ),
                renderCell: params => (
                    <IconButton
                        aria-label="Save"
                        onClick={() => handleUpdateClick([params.row], 'PUT')}
                        style={{
                            width: '100%',
                            verticalAlign: 'middle',
                            color: rowEditionDisabled(params.row) ? '#B8B8B8' : '#f19100',
                        }}
                        disabled={rowEditionDisabled(params.row)}
                        size="large">
                        <SaveSharp />
                    </IconButton>
                ),
            },
            {
                field: 'deleteButton',
                headerName: '',
                width: 60,
                headerAlign: 'center',
                sortable: false,
                disableExport: true,
                resizable: false,
                renderHeader: () => (
                    <IconButton
                        aria-label="Delete"
                        style={{ width: '100%', verticalAlign: 'middle', color: '#676665' }}
                        size="large">
                        <DeleteSharp />
                    </IconButton>
                ),

                renderCell: params => (
                    <IconButton
                        aria-label="Delete"
                        onClick={() => handleUpdateClick([params.row], 'DELETE')}
                        style={{
                            width: '100%',
                            verticalAlign: 'middle',
                            color: rowEditionDisabled(params.row) ? '#B8B8B8' : '#f19100',
                        }}
                        disabled={rowEditionDisabled(params.row)}
                        size="large">
                        <DeleteSharp />
                    </IconButton>
                ),
            },
        ]
    }

    const useStyles = makeStyles(
        () => {
            return {
                root: {
                    '& .MuiDataGrid-root': {
                        border: 'none',
                    },
                    '& .MuiPaginationItem-root': {
                        borderRadius: 0,
                    },
                    '& .MuiDataGrid-row': {
                        backgroundColor: '#F9F9F9',
                        borderRadius: '7px',
                        marginBottom: '6px',
                    },
                    '& .MuiDataGrid-cell': {
                        borderBottom: 'none',
                        overflow: 'auto',
                    },
                    '& .wrapHeader .MuiDataGrid-colCellTitle': {
                        overflow: 'hidden',
                        whiteSpace: 'normal',
                    },
                    '& .MuiDataGrid-virtualScrollerContent': {
                        'padding-bottom': '80px !important',
                    },
                },
            }
        },
        { _theme }
    )

    const classes = useStyles()

    return <ThemeProvider theme={_theme}>
        <div style={{ display: 'flex', height: '100%' }} className="gridContainer">
            {loading || !props.rows ? (
                <CircularProgress
                    style={{
                        position: 'absolute',
                        top: '48%',
                        left: '48%',
                        width: '4%',
                        height: 'auto',
                    }}
                />
            ) : (
                <div style={{ flexGrow: 1, height: '100%', overflow: 'auto !important' }} className={classes.root}>
                    <DataGridPro
                        pagination
                        rowHeight={70}
                        headerHeight={70}
                        page={page}
                        pageSize={pageSize}
                        rowsPerPageOptions={[15, 50, 100]}
                        onPageSizeChange={newPageSize => {
                            setPageSize(newPageSize)
                            setPage(0)
                            pagesNextCursor.current = {}
                        }}
                        onPageChange={handlePageChange}
                        rows={filterByValue(props.rows, props.searchText)}
                        columns={columns}
                        checkboxSelection
                        isRowSelectable={() => Number(outageIndicator) === 0}
                        disableSelectionOnClick
                        onRowClick={row => props.setRowClicked(row.id)}
                        density="compact"
                        paginationMode="server"
                        disableColumnMenu={true}
                        disableColumnFilter
                        onSelectionModelChange={newSelection => {
                            setSelectionModel(newSelection)
                        }}
                        rowCount={pageSize * (page + 1) + pageSize}
                        selectionModel={selectionModel}
                        components={{
                            Toolbar: GridToolbar,
                        }}
                    />
                    <RFDocumentTemplateLink style={{
                        position: 'absolute',
                        top: '10px',
                        left: '300px',
                        zIndex: 10, // display on top of datagrid
                    }} />
                </div>
            )}
        </div>
    </ThemeProvider>;
}

BCPDataGrid.propTypes = {
    filters: PropTypes.object,
    reload: PropTypes.object,
    searchText: PropTypes.string,
    rows: PropTypes.array,
    setReload: PropTypes.func,
    multipleEdit: PropTypes.func,
    throwError: PropTypes.func,
    setRows: PropTypes.func,
    setOpenDescription: PropTypes.func,
    setCurrentDescription: PropTypes.func,
    setOpenDocument: PropTypes.func,
    setCurrentDocument: PropTypes.func,
    setRowClicked: PropTypes.func,
    handleClickReasonCode: PropTypes.func,
    handleClickExpiry: PropTypes.func,
}
